import React, { useState, useEffect } from 'react';
import { Card, Tabs, Input, Button, Table, message, Space, Modal, InputNumber, Form, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined, SearchOutlined } from '@ant-design/icons';
import { examService } from '../../../services/examService';
import { STUDY_REQUIREMENTS } from '../../../services/studentService';

const { Search } = Input;
const { TabPane } = Tabs;

const StudyProgress = () => {
  const [activeTab, setActiveTab] = useState('科目一');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  // Separate lists for each subject
  const [studentLists, setStudentLists] = useState({
    '科目一': [],
    '科目二': [],
    '科目三': [],
    '科目四': []
  });
  const [updateModal, setUpdateModal] = useState({
    visible: false,
    student: null,
    minutes: 0,
    kilometers: 0
  });
  const [bulkUpdateModal, setBulkUpdateModal] = useState({
    visible: false,
    minutes: 0,
    kilometers: 0
  });

  // Fetch students when tab changes
  useEffect(() => {
    if (!searchValue) {
      fetchIncompleteStudents();
    }
  }, [activeTab]);

  // Columns for the student table
  const columns = [
    { title: '档案号', dataIndex: 'fileId' },
    { title: '姓名', dataIndex: 'name' },
    { title: '身份证号', dataIndex: 'uniqueId' },
    { title: '电话', dataIndex: 'phone' },
    { 
      title: '当前进度', 
      dataIndex: 'studyProgress',
      render: (progress, record) => {
        const requirements = STUDY_REQUIREMENTS[activeTab];
        if (activeTab === '科目二' || activeTab === '科目三') {
          return (
            <div>
              <div>学时：{progress?.minutes || 0}/{requirements.minutes}分钟</div>
              <div>里程：{progress?.kilometers || 0}/{requirements.kilometers}公里</div>
            </div>
          );
        }
        return `${progress?.minutes || 0}/${requirements.minutes}分钟`;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => setUpdateModal({ visible: true, student: record, minutes: 0, kilometers: 0 })}
          >
            更新进度
          </Button>
        </Space>
      )
    }
  ];

  // Handle single student hour update
  const handleUpdateProgress = async () => {
    const { student, minutes, kilometers } = updateModal;
    console.log(minutes, kilometers);
    if (!minutes || minutes <= 0) {
      message.error('请输入有效的学时数');
      return;
    }
    if ((activeTab === '科目二' || activeTab === '科目三') && (kilometers === null || kilometers === undefined || kilometers < 0)) {
      message.error('请输入有效的里程数');
      return;
    }

    try {
      setLoading(true);
      await examService.updateStudyHours(
        [student.objectId], 
        activeTab, 
        minutes,  // No longer need to multiply by 60
        kilometers
      );
      message.success('更新成功');
      setUpdateModal({ visible: false, student: null, minutes: 0, kilometers: 0 });
      
      if (searchValue) {
        handleSearch(searchValue);
      } else {
        fetchIncompleteStudents();
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk update
  const handleBulkUpdate = async () => {
    const { minutes, kilometers } = bulkUpdateModal;
    if (!minutes || minutes <= 0) {
      message.error('请输入有效的学时数');
      return;
    }
    if ((activeTab === '科目二' || activeTab === '科目三') && (kilometers === null || kilometers === undefined || kilometers < 0)) {
      message.error('请输入有效的里程数');
      return;
    }

    try {
      setLoading(true);
      await examService.updateStudyHours(
        studentLists[activeTab].map(s => s.objectId),
        activeTab,
        minutes,
        kilometers
      );
      message.success('批量更新成功');
      setBulkUpdateModal({ visible: false, minutes: 0, kilometers: 0 });
      fetchIncompleteStudents();
    } catch (error) {
      message.error('批量更新失败');
    } finally {
      setLoading(false);
    }
  };

  // Save current list
  const handleSaveList = async () => {
    if (!studentLists[activeTab].length) {
      message.error('没有可保存的学员');
      return;
    }

    try {
      setLoading(true);
      await examService.saveStudyProgressList(activeTab, studentLists[activeTab]);
      message.success('名单保存成功');
    } catch (error) {
      message.error('名单保存失败');
    } finally {
      setLoading(false);
    }
  };

  // Fetch incomplete students for current subject
  const fetchIncompleteStudents = async () => {
    try {
      setLoading(true);
      console.log('Fetching incomplete students for:', activeTab);
      const fetchedStudents = await examService.getIncompleteStudents(activeTab);
      console.log('Fetched students count:', fetchedStudents.length);
      
      // Debug: Check if the specific student is in the results
      const targetStudentId = "232332200104260016";
      const foundTarget = fetchedStudents.some(s => s.uniqueId === targetStudentId);
      console.log('Is target student in results?', foundTarget);
      
      if (fetchedStudents.length > 0) {
        console.log('Sample student in results:', fetchedStudents[0]);
      }
      
      // Update only the current subject's list
      setStudentLists(prev => ({
        ...prev,
        [activeTab]: fetchedStudents
      }));
    } catch (error) {
      console.error('Error fetching students:', error);
      message.error('获取学员列表失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (value) => {
    try {
      setLoading(true);
      setSearchValue(value);
      if (!value) {
        fetchIncompleteStudents();
        return;
      }
      const students = await examService.searchStudent(value);
      console.log('Search results:', students);
      
      if (students && students.length > 0) {
        setStudentLists(prev => ({
          ...prev,
          [activeTab]: students
        }));
      } else {
        setStudentLists(prev => ({
          ...prev,
          [activeTab]: []
        }));
        message.info('未找到学员');
      }
    } catch (error) {
      message.error('搜索失败');
    } finally {
      setLoading(false);
    }
  };

  // Get current subject's student list
  const getCurrentStudents = () => studentLists[activeTab] || [];

  const handleImport = async (file) => {
    try {
      setLoading(true);
      const result = await examService.importStudyProgress(file);
      const { stats, failedRecords } = result;
      
      message.success(
        `导入完成: 总计 ${stats.total} 条记录, ` +
        `成功 ${stats.successful} 条, ` +
        `失败 ${stats.errors} 条`
      );

      // If there are failed records, show them in a modal
      if (failedRecords.length > 0) {
        Modal.info({
          title: '导入失败记录',
          width: 600,
          content: (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table 
                dataSource={failedRecords}
                columns={[
                  { title: '行号', dataIndex: 'row' },
                  { title: '身份证号', dataIndex: 'uniqueId' },
                  { title: '失败原因', dataIndex: 'reason' }
                ]}
                pagination={false}
                size="small"
              />
            </div>
          )
        });
      }

      fetchIncompleteStudents();
      return false;
    } catch (error) {
      console.error('Import error:', error);
      message.error('学时导入失败: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="学时更新">
      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="搜索学员"
          prefix={<SearchOutlined />}
          style={{ width: 200 }}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <Upload
          beforeUpload={handleImport}
          showUploadList={false}
          accept=".xlsx,.xls"
        >
          <Button 
            icon={<UploadOutlined />}
            loading={loading}
          >
            批量导入学时
          </Button>
        </Upload>
      </Space>

      <Tabs 
        activeKey={activeTab} 
        onChange={(key) => {
          setActiveTab(key);
          setSearchValue(''); // Clear search when changing tabs
        }}
      >
        {['科目一', '科目二', '科目三', '科目四'].map(subject => (
          <TabPane tab={subject} key={subject}>
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Space>
                <Search
                  placeholder="输入档案号/姓名/电话搜索"
                  onSearch={handleSearch}
                  style={{ width: 300 }}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button 
                  type="primary" 
                  onClick={() => {
                    setSearchValue('');
                    fetchIncompleteStudents();
                  }}
                >
                  获取未完成学员
                </Button>
              </Space>
              
              <Space>
                <Button 
                  onClick={() => setBulkUpdateModal({ visible: true, minutes: 0, kilometers: 0 })}
                  disabled={!getCurrentStudents().length}
                >
                  批量更新
                </Button>
                <Button
                  onClick={handleSaveList}
                  disabled={!getCurrentStudents().length}
                >
                  保存名单
                </Button>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={() => examService.exportStudentsToExcel(getCurrentStudents(), '学时更新名单')}
                  disabled={!getCurrentStudents().length}
                >
                  导出名单
                </Button>
              </Space>
            </Space>

            <Table
              columns={columns}
              dataSource={getCurrentStudents()}
              rowKey="objectId"
              loading={loading}
            />
          </TabPane>
        ))}
      </Tabs>

      {/* Single student update modal */}
      <Modal
        title="更新进度"
        open={updateModal.visible}
        onOk={handleUpdateProgress}
        onCancel={() => setUpdateModal({ visible: false, student: null, minutes: 0, kilometers: 0 })}
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <p>学员：{updateModal.student?.name}</p>
          <Form.Item label="新增学时（分钟）" required>
            <InputNumber
              min={1}
              value={updateModal.minutes}
              onChange={minutes => setUpdateModal(prev => ({ ...prev, minutes }))}
            />
          </Form.Item>
          {(activeTab === '科目二' || activeTab === '科目三') && (
            <Form.Item label="新增里程（公里）" required>
              <InputNumber
                min={0}
                value={updateModal.kilometers}
                onChange={kilometers => setUpdateModal(prev => ({ ...prev, kilometers }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Bulk update modal */}
      <Modal
        title="批量更新进度"
        open={bulkUpdateModal.visible}
        onOk={handleBulkUpdate}
        onCancel={() => setBulkUpdateModal({ visible: false, minutes: 0, kilometers: 0 })}
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <p>选中学员数：{studentLists[activeTab].length}</p>
          <Form.Item label="新增学时（分钟）" required>
            <InputNumber
              min={1}
              value={bulkUpdateModal.minutes}
              onChange={minutes => setBulkUpdateModal(prev => ({ ...prev, minutes }))}
            />
          </Form.Item>
          {(activeTab === '科目二' || activeTab === '科目三') && (
            <Form.Item label="新增里程（公里）" required>
              <InputNumber
                min={0}
                value={bulkUpdateModal.kilometers}
                onChange={kilometers => setBulkUpdateModal(prev => ({ ...prev, kilometers }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default StudyProgress; 